/**
 * Shared utility functions for custom form processing
 * Used by both DynamicFormPreview and DynamicForm components
 */

import { SELF_RELATIONSHIP_TYPE_ID } from '@sportninja/common/features/profile/family/constants'

import { FormGroup, FormItem, FORM_ITEM_TYPES } from '../types/CustomForm'

/** Label shown for the Self ("Me") relationship option in the dynamic form's
 * relationship dropdown (new-player mode). The BE names this entry "Me"; here we
 * relabel it to make the action explicit — selecting it marks the family contact
 * as the player themselves. */
export const SELF_RELATIONSHIP_OPTION_LABEL = '* Mark As Me'

/**
 * Filters form groups to only include enabled groups with enabled items.
 * Considers both 1/0 and true/false for is_enabled.
 * @param groups - Array of form groups to filter
 * @returns Array of enabled groups with enabled items
 *
 * @example
 * ```typescript
 * const enabledGroups = filterEnabledGroups(formGroups)
 * ```
 */
export const filterEnabledGroups = (groups: FormGroup[]): FormGroup[] => {
  /**
   * Checks if a group or item is enabled (1 or true).
   * @param value - The is_enabled value to check
   * @returns boolean
   */
  const isEnabled = (value: number | boolean): boolean =>
    value === 1 || value === true
  return groups
    .filter((group) => isEnabled(group.is_enabled))
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isEnabled(item.is_enabled)),
    }))
    .filter((group) => {
      // Allow content sections to pass through even if they have no items
      const isContentGroup = (group as any).is_content
      return group.items.length > 0 || isContentGroup
    })
}

/**
 * Sorts groups by their index property
 * @param groups - Array of form groups to sort
 * @returns Sorted array of groups
 *
 * @example
 * ```typescript
 * const sortedGroups = sortGroupsByIndex(formGroups)
 * ```
 */
export const sortGroupsByIndex = (groups: FormGroup[]): FormGroup[] => {
  return [...groups].sort((a, b) => a.index - b.index)
}

/**
 * Sorts items within a group by their index property
 * @param group - Form group containing items to sort
 * @returns Group with sorted items
 *
 * @example
 * ```typescript
 * const groupWithSortedItems = sortItemsByIndex(formGroup)
 * ```
 */
export const sortItemsByIndex = (group: FormGroup): FormGroup => {
  return {
    ...group,
    items: [...group.items].sort((a, b) => a.index - b.index),
  }
}

/**
 * Maps form item type ID to a human-readable kind string
 * @param id - Form item type ID
 * @returns String representing the field type
 *
 * @example
 * ```typescript
 * const kind = mapFormItemTypeToKind(FORM_ITEM_TYPES.TEXT_FIELD) // returns 'text'
 * ```
 */
export const mapFormItemTypeToKind = (
  id: number
):
  | 'text'
  | 'number'
  | 'dropdown'
  | 'checkbox'
  | 'textarea'
  | 'date'
  | 'birthdate'
  | 'phone'
  | 'email'
  | 'url'
  | 'country'
  | 'playerPosition'
  | 'hockey_usa'
  | 'player_number'
  | 'relationship'
  | 'teamOfficialRole' => {
  switch (id) {
    case FORM_ITEM_TYPES.TEXT_FIELD:
      return 'text'
    case FORM_ITEM_TYPES.NUMBER_FIELD:
      return 'number'
    case FORM_ITEM_TYPES.DROPDOWN:
      return 'dropdown'
    case FORM_ITEM_TYPES.CHECKBOX:
      return 'checkbox'
    case FORM_ITEM_TYPES.TEXT_AREA:
      return 'textarea'
    case FORM_ITEM_TYPES.DATE:
      return 'date'
    case FORM_ITEM_TYPES.BIRTH_DATE:
      return 'birthdate'
    case FORM_ITEM_TYPES.PHONE:
      return 'phone'
    case FORM_ITEM_TYPES.EMAIL:
      return 'email'
    case FORM_ITEM_TYPES.URL:
      return 'url'
    case FORM_ITEM_TYPES.COUNTRY:
      return 'country'
    case FORM_ITEM_TYPES.PLAYER_POSITION:
      return 'playerPosition'
    case FORM_ITEM_TYPES.HOCKEY_USA:
      return 'hockey_usa'
    case FORM_ITEM_TYPES.PLAYER_NUMBER:
      return 'player_number'
    case FORM_ITEM_TYPES.RELATIONSHIP:
      return 'relationship'
    case FORM_ITEM_TYPES.TEAM_OFFICIAL_ROLE:
      return 'teamOfficialRole'
    default:
      return 'text'
  }
}

/**
 * Normalizes a string to be used as a value:
 * - Converts to lowercase
 * - Replaces all whitespace with underscores
 * @param name - The string to normalize
 * @returns The normalized string
 *
 * @example
 * ```typescript
 * normalizeNameForValue('First Name') // returns 'first_name'
 * normalizeNameForValue('  Option 2 ') // returns 'option_2'
 * ```
 */
export function normalizeNameForValue(name: string): string {
  if (!name) {
    return ''
  }
  return name.trim().toLowerCase().replace(/\s+/g, '_')
}

/**
 * Extracts dropdown options from form item parameters
 * @param item - Form item containing parameters with values
 * @returns Array of options with label and value properties
 *
 * @example
 * ```typescript
 * const options = extractOptions(dropdownItem)
 * // returns [{ label: 'Option 1', value: 'option1' }, ...]
 * ```
 */
export const extractOptions = (
  item: FormItem
): Array<{ label: string; value: string }> => {
  if (!item.parameters?.values) {
    return []
  }

  return item.parameters.values
    .sort((a, b) => a.index - b.index) // Sort by index
    .map((value) => ({
      label: value.name,
      // API may send numeric values (e.g. relationship: 1, 2, 4) — coerce to
      // string so it stays consistent with SelectInput and read-only matching.
      value: String(value.value),
    }))
}

/**
 * Relabels the Self ("Me") option in a relationship dropdown's options to
 * "Mark As Me", leaving every other option untouched. Matched by the BE Self
 * relationship id (value 6) rather than the label, so copy changes stay safe.
 * Non-mutating — returns a new array.
 * @param options - Relationship options as produced by extractOptions
 * @returns A new options array with the Self option relabeled (if present)
 */
export const relabelSelfRelationshipOption = (
  options: Array<{ label: string; value: string }>
): Array<{ label: string; value: string }> =>
  options.map((option) =>
    option.value === String(SELF_RELATIONSHIP_TYPE_ID)
      ? { ...option, label: SELF_RELATIONSHIP_OPTION_LABEL }
      : option
  )

/**
 * Extracts number range constraints from form item parameters
 * @param item - Form item containing min/max parameters
 * @returns Object with optional min and max number values
 *
 * @example
 * ```typescript
 * const range = extractNumberRange(numberItem)
 * // returns { min: 1, max: 100 }
 * ```
 */
export const extractNumberRange = (
  item: FormItem
): { min?: number; max?: number } => {
  if (!item.parameters) {
    return {}
  }

  // Handle both old and new parameter formats
  const minValue = item.parameters.min_number || item.parameters.min
  const maxValue = item.parameters.max_number || item.parameters.max

  return {
    min: minValue ? Number(minValue) : undefined,
    max: maxValue ? Number(maxValue) : undefined,
  }
}

/**
 * Extracts max character constraint from form item parameters
 * @param item - Form item containing parameters with max_char
 * @returns Number of max characters
 */
export const extractMaxChar = (item: FormItem): number => {
  if (!item.parameters) {
    return 0
  }
  return item.parameters.max_char ? Number(item.parameters.max_char) : 0
}

/**
 * Formats field label with character limit information when applicable
 * @param item - Form item containing name and parameters
 * @returns Formatted label string with character limit in parentheses if applicable
 *
 * @example
 * ```typescript
 * const label = formatFieldLabel(item)
 * // returns "Short Answer (50 characters max)" if max_char is 50
 * // returns "Short Answer *" if required and no max_char
 * ```
 */
export const formatFieldLabel = (item: FormItem): string => {
  // Handles is_required as 1, true, 0, or false
  const baseName = item.display_label || item.name
  const isRequired = item.is_required === 1 || item.is_required === true

  const maxChar = extractMaxChar(item)

  let label = baseName

  // Add character limit info if specified
  if (maxChar > 0) {
    label += ` (${maxChar} characters max)`
  }

  // Add required asterisk
  if (isRequired) {
    label += ' *'
  }

  return label
}
